// Shared phone chrome: the bottom tab bar every audience shell now uses, and a bottom sheet that
// replaces the "throw up a modal (or worse, window.prompt) for everything" pattern.
//
// These live in one place because the staff console, the clinic portal and the patient app were
// each inventing their own bar — with different heights, different safe-area handling and, in the
// portal's case, none at all. One implementation means a notch or a home indicator is handled once.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { IconClose } from './icons.tsx';

/** Lock the page behind an overlay without letting the body jump to the top on iOS. */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [active]);
}

/* -- bottom tab bar -------------------------------------------------------------------------- */

export interface TabDef {
  /** Route to navigate to. Omit and pass `onClick` for a tab that opens the menu or a sheet. */
  to?: string;
  end?: boolean;
  onClick?: () => void;
  label: string;
  icon: (p: { size?: number; filled?: boolean }) => ReactNode;
  /** Small count on the glyph — unread chat, cases awaiting you. */
  badge?: number;
}

/**
 * The phone tab bar. Renders `tabs` around an optional raised centre action.
 *
 * Deliberately not translucent-by-default: a blurred bar over a white list reads as smudged on a
 * low-end Android. It gets a solid themed surface, a hairline, and a shadow that lifts it off the
 * content — plus the home-indicator inset so the labels are never under the gesture bar.
 */
export function TabBar({
  tabs,
  action,
  className = '',
}: {
  tabs: TabDef[];
  action?: { label: string; icon: (p: { size?: number }) => ReactNode; onClick: () => void };
  className?: string;
}) {
  const cols = tabs.length + (action ? 1 : 0);
  const left = action ? tabs.slice(0, Math.ceil(tabs.length / 2)) : tabs;
  const right = action ? tabs.slice(Math.ceil(tabs.length / 2)) : [];
  return (
    <nav
      className={`fixed inset-x-0 bottom-0 z-bar grid items-end bg-surface pb-safe shadow-bar hairline-t lg:hidden ${className}`}
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      aria-label="Primary"
    >
      {left.map((t) => (
        <TabLink key={t.to ?? t.label} {...t} />
      ))}
      {action && (
        <div className="grid place-items-center">
          <button
            type="button"
            onClick={action.onClick}
            aria-label={action.label}
            className="tap relative -top-5 grid h-14 w-14 place-items-center rounded-full bg-accent text-accent-fg shadow-level-3 ring-4 ring-bg"
          >
            {action.icon({ size: 26 })}
          </button>
        </div>
      )}
      {right.map((t) => (
        <TabLink key={t.to ?? t.label} {...t} />
      ))}
    </nav>
  );
}

const TAB_CLS = 'tap flex h-tabbar flex-col items-center justify-center gap-1 text-[11px] font-medium tracking-tight';

function TabGlyph({
  icon,
  badge,
  active,
  label,
}: {
  icon: TabDef['icon'];
  badge?: number;
  active: boolean;
  label: string;
}) {
  return (
    <>
      <span className="relative grid place-items-center">
        {icon({ size: 23, filled: active })}
        {!!badge && badge > 0 && (
          <span className="tabnum absolute -right-2.5 -top-1.5 min-w-[1.05rem] rounded-full bg-status-urgent-fg px-1 text-center text-[10px] font-bold leading-4 text-white">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      {label}
    </>
  );
}

function TabLink({ to, end, onClick, label, icon, badge }: TabDef) {
  if (!to) {
    return (
      <button type="button" onClick={onClick} className={`${TAB_CLS} text-muted`}>
        <TabGlyph icon={icon} badge={badge} active={false} label={label} />
      </button>
    );
  }
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `${TAB_CLS} ${isActive ? 'text-accent' : 'text-muted'}`}>
      {({ isActive }) => <TabGlyph icon={icon} badge={badge} active={isActive} label={label} />}
    </NavLink>
  );
}

/** Spacer so the last row of a scrolling page clears the bar. Put it at the end of the content. */
export function TabBarSpacer() {
  return <div className="pb-bar lg:hidden" aria-hidden="true" />;
}

/* -- bottom sheet ---------------------------------------------------------------------------- */

/**
 * A sheet that rises from the bottom on phones and centres as a dialog from `sm` up. Used for the
 * things that used to be a `window.prompt` or a full-screen modal: search, filters, quick actions.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  /** Focus this on open — the search field, usually. */
  initialFocus,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  initialFocus?: React.RefObject<HTMLElement | null>;
}) {
  const panel = useRef<HTMLDivElement>(null);
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const id = window.setTimeout(() => initialFocus?.current?.focus(), 60);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(id);
    };
  }, [open, onClose, initialFocus]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-modal flex items-end justify-center bg-navy/50 animate-fade sm:items-center sm:p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        ref={panel}
        className="animate-sheet flex max-h-[88dvh] w-full flex-col rounded-sheet bg-surface shadow-level-3 sm:max-w-lg sm:rounded-lg"
      >
        {/* Grab handle: the affordance that tells a thumb this panel came from the bottom edge. */}
        <div className="grid place-items-center pt-2.5 sm:hidden">
          <span className="h-1 w-10 rounded-full bg-line" />
        </div>
        <div className="flex items-center justify-between gap-3 px-4 pb-2 pt-3 sm:border-b sm:border-line sm:py-3">
          <h2 className="text-base font-semibold text-ink">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="tap grid h-9 w-9 place-items-center rounded-lg text-muted hover:bg-surface-2"
          >
            <IconClose size={20} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">{children}</div>
        {footer && <div className="hairline-t px-4 py-3 pb-safe">{footer}</div>}
      </div>
    </div>
  );
}

/* -- page furniture -------------------------------------------------------------------------- */

/**
 * Phone page header. Big on mobile where it is the only orientation the user gets, quieter on
 * desktop where the nav rail already says where they are.
 */
export function MobileTitle({
  title,
  sub,
  action,
}: {
  title: string;
  sub?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-4 flex items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="truncate text-[1.6rem] font-bold leading-tight tracking-tight text-ink lg:text-2xl">
          {title}
        </h1>
        {sub && <p className="mt-0.5 truncate text-sm text-muted">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

/** Section label — sentence case, quiet, with an optional trailing link. */
export function SectionLabel({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-3">
      <h2 className="text-[13px] font-semibold text-muted">{children}</h2>
      {right}
    </div>
  );
}

/** Skeleton block that matches the shape of the card it stands in for. */
export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`skeleton ${className}`} aria-hidden="true" />;
}

/** A list of skeleton rows, for lists that are loading. */
export function SkeletonList({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2" role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="rounded-lg border border-line bg-surface p-4">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="mt-2 h-3 w-3/4" />
        </div>
      ))}
    </div>
  );
}

/**
 * True while the viewport is phone/tablet width (below Tailwind's `lg`). Use it only where markup
 * must genuinely differ — a filter panel that is inline on a desk and a sheet on a phone — not for
 * styling, which belongs in the responsive classes.
 */
export function useIsPhone(): boolean {
  const [phone, setPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const on = () => setPhone(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return phone;
}
